import './sepho-race.css';
import { renderGameMessage, startBlockedMessage } from '../shared/game-message';

const SCRIPT_URL = '/build/games/sepho-race/sepho-race.min.js';

import imgPlayer from './images/player.png';
import imgBackground from './images/background.webp';
import imgLifeIndicator from './images/heart-full.svg';
import imgLostLifeIndicator from './images/heart-empty.svg';
import imgCollectible1 from './images/collectible-1.png';
import imgCollectible2 from './images/collectible-2.png';
import imgCollectible3 from './images/collectible-3.png';

import sndCorrect from './sounds/correct.wav';
import sndWrong from './sounds/wrong.wav';

import imgGameIcon from '../../images/game-icon.svg';

let gameInstance  = null;
let currentTaskId = null;
let sessionToken  = null;
let eventSeq      = 0;
let _startTime    = null;
let _pausedMs     = 0;
let _pauseStart   = null;

const DEFAULT_GAME_CONFIG = {
    zSpeedFactor:            0.12,
    spawnZ:                  0.08,
    scorePerMeter:           1,
    collectibleScore:        10,
    collectibleGap:          0.04,
    laneChangeTime:          0.10,
    jumpVelocity:            -520,
    jumpGravity:             1500,
    jumpAirThreshold:        18,
    spawnIntervalBase:       10,
    minSpawnInterval:        0.28,
    wideObstacleChance:      0.28,
    collectibleIntervalBase: 7,
    minCollectibleInterval:  0.35,
};

const DEFAULT_ASSETS_CONFIG = {
    labels: {
        title: 'Pack the Glow',
        subtitle: 'Omijaj przeszkody i zbieraj produkty!',
        controls: '← → zmiana pasa  •  ↑ / spacja skok',
        btnStart: 'Start!',
        resultTitle: 'Koniec biegu!',
        statScore: 'Wynik',
        statDistance: 'Dystans',
        statRankingPoints: 'Punkty rankingowe',
        statRedeemPoints: 'Punkty do wymiany',
        btnRestart: 'Zagraj ponownie',
        hudScore: 'PUNKTY',
    },
    images: {
        player: imgPlayer,
        background: imgBackground,
        // obstacle: imgObstacle,
        collectibles: [
            imgCollectible1, imgCollectible2, imgCollectible3,
        ],
        lifeIndicator: imgLifeIndicator,
        lostLifeIndicator: imgLostLifeIndicator,
    },
    sounds: {
        collect: sndCorrect,
        hit: sndWrong,
    },
    colors: {
        // Road
        road:              '#C1C1C1',
        roadTop:           '#4B4B4B',
        roadEdge:          '#231f20',
        gridLine:          'rgba(123, 63, 255, 0.18)',
        laneLine:          '#ffffff',

        // Player (procedural fallback)
        player:            '#e91e8c',
        playerShade:       '#b2136b',

        // Obstacle (procedural fallback + burst)
        obstacle: ['#ffd400', '#ffa700'],
        obstacleHighlight: 'rgba(255,160,160,0.55)',
        obstacleHalo:      'rgba(255,45,45,0.18)',

        // Collectible (procedural fallback + burst)
        collectible:       '#ffd700',
        collectibleHalo:   'rgba(255,215,0,0.18)',
    },
};

export const WELCOME_CONFIG = {
    icon: imgGameIcon,
    title: 'WITAJ W PACK THE GLOW',
    description: '<p>Przygotuj się na dynamiczną grę, w której liczy się refleks, koncentracja i szybka reakcja! ' +
        'Walizka porusza się automatycznie do przodu po trzypasmowej drodze, a Twoim zadaniem jest omijanie przeszkód i zbieranie hot produktów.</p>' +
        '<p>Uważaj jednak – prędkość rośnie z czasem, więc trasa staje się coraz trudniejsza. Masz 3 życia – każda kolizja z przeszkodą odbiera 1 życie. Utrata wszystkich 3 kończy grę.</p>',
    howToPlay: {
        heading: 'JAK GRAĆ?',
        body: '<ol>' +
            '<li>Zmieniaj pas w lewo lub w prawo i skacz, aby omijać przeszkody.</li>' +
            '<li>Zbieraj hot produkty pojawiające się na drodze.</li>' +
            '<li>Dotrzyj jak najdalej i postaraj się zdobyć jak najlepszy wynik!</li>' +
            '</ol>' +
            '<p>Ruszaj na trasę i sprawdź, jak daleko uda Ci się dotrzeć! 🔥✨</p>',
    },
    scoring: {
        heading: 'PUNKTACJA',
        body: '<ul>' +
            '<li>✅ Punkty naliczają się automatycznie za przebyty dystans.</li>' +
            '<li>🔥 Każdy zebrany hot produkt dodaje punkty do wyniku.</li>' +
            '<li>❌ Kolizja z przeszkodą odbiera 1 życie (3 życia łącznie – utrata wszystkich kończy grę).</li>' +
            '</ul>' +
            '<p>Maksymalny wynik to 40 punktów.<br />⏱️ Gra nie ma limitu czasu – trwa tak długo, jak uda Ci się unikać przeszkód.</p>',
    },
};

function resetSessionState() {
    currentTaskId = null;
    sessionToken  = null;
    eventSeq      = 0;
    _startTime    = null;
    _pausedMs     = 0;
    _pauseStart   = null;
}

function getElapsedMs() {
    if (_startTime === null) return 0;
    const activePause = _pauseStart !== null ? Date.now() - _pauseStart : 0;
    return Math.max(0, Date.now() - _startTime - _pausedMs - activePause);
}

function sendEvent(path, taskId, body) {
    if (!sessionToken) return;
    fetch(`/api/task/${taskId}/sepho-race/${path}`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ sessionToken, ...body }),
    }).catch(() => { /* fire-and-forget */ });
}

function attachEventListeners(taskId) {
    if (!gameInstance?.eventBus) return;

    gameInstance.eventBus.on('game:start', () => {
        sessionToken = gameInstance.sessionToken ?? null;
        eventSeq     = 0;
        _startTime   = Date.now();
        _pausedMs    = 0;
        _pauseStart  = null;
    });

    gameInstance.eventBus.on('obstacle:hit', ({ livesRemaining }) => {
        if (!sessionToken) return;
        eventSeq++;
        sendEvent('obstacle-hit', taskId, {
            seq:            eventSeq,
            elapsedMs:      getElapsedMs(),
            score:          gameInstance.score          ?? 0,
            distanceMeters: gameInstance.distanceMeters ?? 0,
            livesRemaining: livesRemaining               ?? 0,
        });
    });

    gameInstance.eventBus.on('game:pause', () => {
        if (!sessionToken) return;
        _pauseStart = Date.now();
        eventSeq++;
        sendEvent('pause', taskId, { seq: eventSeq, elapsedMs: getElapsedMs() });
    });

    gameInstance.eventBus.on('game:resume', () => {
        if (!sessionToken) return;
        if (_pauseStart !== null) {
            _pausedMs  += Date.now() - _pauseStart;
            _pauseStart = null;
        }
        eventSeq++;
        sendEvent('resume', taskId, { seq: eventSeq, elapsedMs: getElapsedMs() });
    });
}

function loadScript(src) {
    return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
            resolve();
            return;
        }
        const s = document.createElement('script');
        s.src = src;
        s.onload = resolve;
        s.onerror = reject;
        document.head.appendChild(s);
    });
}

function contentEl(modal) {
    return modal.querySelector('.game-modal__content');
}

export function renderSephoRace(modal, data, markCardDone) {
    const el = contentEl(modal);
    el.innerHTML = '';
    resetSessionState();
    startGame(modal, data, markCardDone);
}

function startGame(modal, data, markCardDone) {
    const taskId = data.task.id;
    currentTaskId = taskId;
    const cfg = data.sephoRaceGame.config;

    const assetsConfig = {
        ...DEFAULT_ASSETS_CONFIG,
        images: {
            ...DEFAULT_ASSETS_CONFIG.images,
            player: cfg.playerImage ?? imgPlayer,
            collectibles: cfg.collectibles?.length > 0
                ? cfg.collectibles
                : [imgCollectible1, imgCollectible2, imgCollectible3],
        },
    };

    const el = contentEl(modal);

    const containerId = 'sr-game-container';
    const container = document.createElement('div');
    container.id = containerId;
    container.className = 'sr-game';
    el.appendChild(container);

    loadScript(SCRIPT_URL)
        .then(() => {
            if (typeof SephoRaceGame === 'undefined') {
                console.error('SephoRaceGame: script loaded but SephoRaceGame is not defined');
                return;
            }

            gameInstance = SephoRaceGame.init(containerId, {
                assetsConfig,
                gameConfig: {
                    ...DEFAULT_GAME_CONFIG,
                    initialSpeed: cfg.initialSpeed ?? 5,
                    speedGrowth:  cfg.speedGrowth  ?? 2,
                    lives:        cfg.lives         ?? 1,
                    autoStart:    true,
                },
                apiConfig: {
                    useMock: false,
                    endpoints: {
                        startGame:    `/api/task/${taskId}/sepho-race/start`,
                        submitResult: `/api/task/${taskId}/sepho-race/submit`,
                        heartbeat:    `/api/task/${taskId}/sepho-race/heartbeat`,
                    },
                },
                callbacks: {
                    onError: (error) => {
                        if (error?.status === 409 && (error.data?.error === 'already_completed' || error.data?.error === 'game_in_progress')) {
                            renderGameMessage(modal, startBlockedMessage(error.data?.error));
                            return;
                        }
                        document.dispatchEvent(new CustomEvent('game-modal:request-close'));
                        alert(`Błąd gry: ${error}`);
                    },
                    onComplete: (result) => {
                        if (gameInstance) {
                            try {
                                gameInstance.destroy();
                            } catch { /* ignore */
                            }
                            gameInstance = null;
                        }
                        markCardDone({rankingPointsEarned: result.rankingPointsEarned ?? 0, redeemablePointsEarned: result.redeemablePointsEarned ?? 0, checkIn: result.checkIn});
                        document.dispatchEvent(new CustomEvent('game-modal:result-show', {
                            detail: {
                                rankingPoints: result.rankingPointsEarned ?? 0,
                                redeemablePoints: result.redeemablePointsEarned ?? 0,
                            },
                        }));
                    },
                },
            });
            attachEventListeners(taskId);
        })
        .catch((e) => console.error('SephoRaceGame: failed to load game script', e));
}

export function pauseSephoRace() {
    if (gameInstance) {
        try {
            gameInstance.pause();
        } catch { /* ignore */ }
    }
}

export function resumeSephoRace() {
    if (gameInstance) {
        try {
            gameInstance.resume();
        } catch { /* ignore */ }
    }
}

export async function forceEndSephoRace() {
    if (gameInstance) {
        try {
            if (currentTaskId !== null) {
                await fetch(`/api/task/${currentTaskId}/sepho-race/flag-force-end`, { method: 'POST' });
            }
            gameInstance.forceEnd();
        } catch { /* ignore */ }
    }
}

export function teardownSephoRace(modal) {
    if (gameInstance) {
        try {
            gameInstance.destroy();
        } catch { /* ignore */
        }
        gameInstance = null;
    }
    resetSessionState();
    const el = contentEl(modal);
    if (el) el.innerHTML = '';
}
